import React, { memo, useEffect, useState, useRef } from "react";

import ErrorBoundary from "../../ErrorBoundary";

import SummaryWidget from "../Widgets/Summary/SummaryWidget";
import LayoutControls from "../Widgets/LayoutControls/LayoutControls";
import FileWidget from "../Widgets/File/FileWidget";
import Activities from "../Widgets/Activities/Activities";
import MapWidget from "../Widgets/Map/MapWidget";
import LinkedItemsWidget from "../Widgets/LinkedItems/LinkedItemsWidget";
import LiveCameraWidget from "../Widgets/LiveCamera/LiveCameraWidget";
import CameraDialog from "./components/CameraDialog";
import PinToDialog from "../../SharedComponents/PinToDialog";
import { facilityService, compositeDeviceService } from "client-app-core";
import isEqual from "react-fast-compare";
import $ from "jquery";
import { Translate, getTranslation } from "orion-components/i18n";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { selectedContextSelector } from "orion-components/ContextPanel/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";
import { subscribeFOVs } from "orion-components/GlobalData/Actions";

import { openDialog, closeDialog } from "orion-components/AppState/Actions";
import {
	startLiveCameraStream,
	startActivityStream,
	startAttachmentStream,
	startFOVItemStream,
	startCamerasLinkedItemsStream,
	unsubscribeFromFeed
} from "orion-components/ContextualData/Actions";

import {
	showFOV,
	hideFOV,
	addRemoveFromEvents,
	addRemoveFromCollections,
	updateCameraGlobally
} from "orion-components/SharedActions/cameraProfileActions";
import { createCollection } from "orion-components/SharedActions/commonActions";
import { cameraProfileConfig } from "orion-components/Profiles/Selectors/CameraProfileSelectors";
import { checkPermissionBasedOnFeedId, canManageByApplication } from "orion-components/Profiles/Selectors";
import DetailsWidget from "../Widgets/Details/DetailsWidget";
import { feedInfoSelector } from "../../GlobalData/Selectors";
import { updatePersistedState } from "../../AppState/Persisted/actions";
import { persistedCameraFovs } from "../Selectors/CameraProfileSelectors";
import AlertingZonesWidget from "../Widgets/AlertingZones/AlertingZonesWidget";

const propTypes = {
	disableLinkedItems: PropTypes.bool,
	floorPlansWithFacFeed: PropTypes.bool,
	updateCamera: PropTypes.func,
	selectWidget: PropTypes.func,
	facilityOption: PropTypes.bool,
	disabledLinkedItemTypes: PropTypes.bool,
	widgetsExpandable: PropTypes.bool,
	appData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	endDate: PropTypes.date,
	facilityFeedId: PropTypes.string,
	mapstatus: PropTypes.string,
	locale: PropTypes.string,
	selectFloorPlanOn: PropTypes.func,
	readOnly: PropTypes.bool,
	forReplay: PropTypes.bool
};

const defaultProps = {
	locale: "en",
	selectFloorPlanOn: () => {},
	//The `readonly` and `forReplay` props are exclusively provided to this component by the `replay-app`. To ensure the proper functionality of other apps, it is necessary to set both `readonly` and `forReplay` to `false`.
	readOnly: false,
	forReplay: false
};

const usePrevious = value => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

// cSpell:ignore mapstate mapstatus

const CameraProfile = ({
	disableLinkedItems,
	floorPlansWithFacFeed,
	selectFloorPlanOn,
	updateCamera,
	selectWidget,
	facilityOption,
	disabledLinkedItemTypes,
	appData,
	endDate,
	facilityFeedId,
	widgetsExpandable,
	readOnly,
	forReplay
}) => {
	const dispatch = useDispatch();

	const context = useSelector(state => selectedContextSelector(state));
	const { entity } = context;
	const view = useSelector(state => entity && state.userAppState?.cameraView);
	const contextId = entity?.id;
	const dir = useSelector(state => entity && getDir(state));
	const camera = entity;
	const appId = useSelector(state => entity && state.appId);

	const canAccess = useSelector(state => !readOnly && checkPermissionBasedOnFeedId(state)(camera?.feedId, "config"));
	const canManage = useSelector(
		state => !readOnly && checkPermissionBasedOnFeedId(state)(camera?.feedId, "permissions", "manage")
	);
	const canControl = useSelector(
		state => !readOnly && checkPermissionBasedOnFeedId(state)(camera?.feedId, "permissions", "control")
	);
	const canManageEvents = useSelector(state => canManageByApplication(state, "events-app", "manage"));
	const canPinToCollections = useSelector(state => canManageByApplication(state, "map-app", "manage"));
	const selectedTab = useSelector(state => state?.appManager?.listPanel?.selectedTab);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const FOVSwitch = entity && (appId === "map-app" || appId === "events-app") ? true : false;

	const floorPlansWithFacilityFeed = useSelector(state =>
		floorPlansWithFacFeed ? state.globalData.floorPlanWithFacilityFeedId.floorPlans : null
	);
	const widgetState = useSelector(state => entity && cameraProfileConfig(state));

	const { liveCamera } = context ?? {};
	const { entityType, entityData, feedId } = camera ?? {};

	const { properties, geometry } = entityData ?? {};
	const [cameraGeometry, setCameraGeometry] = useState(geometry);

	useEffect(() => {
		if (camera?.compositeDeviceId) {
			compositeDeviceService.getCompositeDeviceById(camera.compositeDeviceId, (err, res) => {
				if (!err && res.entityData?.geometry) {
					setCameraGeometry(res.entityData.geometry);
				}
			});
		}
	}, []);

	const { name, description, type, subtype } = properties ?? {};

	const [layoutControlsOpen, setLayoutControlsOpen] = useState(false);
	const [scrolledUp, setScrolledUp] = useState(false);
	const [hiding, setHiding] = useState(false);
	const [facilityHidden, setFacilityHidden] = useState(true);
	const [facilityPlacement, setFacilityPlacement] = useState(null);
	const displayProperties = useSelector(state => {
		return (entity && feedInfoSelector(feedId)(state)?.displayProperties) || [];
	});
	const activeFOVs = useSelector(state => persistedCameraFovs(state)(camera?.id) ?? false);
	const isFovVisible = appId === "cameras-app" ? true : context?.entity?.showFOV;

	if (camera) {
		camera.showFOV = activeFOVs;
	}
	useEffect(() => {
		handleScroll();
	}, [scrolledUp]);

	useEffect(() => {
		if (!forReplay) {
			if (!disableLinkedItems && getWidgetStatus("linked_items")) {
				dispatch(startCamerasLinkedItemsStream(contextId, "profile"));
			}
			if (getWidgetStatus("activities")) dispatch(startActivityStream(contextId, "camera", "profile"));
			if (getWidgetStatus("files")) dispatch(startAttachmentStream(contextId, "profile"));
			if (camera?.entityData?.displayTargetId && camera?.entityData?.displayType?.toLowerCase() === "facility") {
				facilityService.getFloorPlan(camera.entityData.displayTargetId, (err, res) => {
					if (err) {
						setFacilityHidden(true);
					} else if (res.success) {
						setFacilityHidden(false);
					}
				});
			}
		} else {
			// do we need to do something else here, or leave it up to the widgets to pull in the static data
		}
		return () => {
			dispatch(selectWidget("map-view"));
		};
	}, []);

	useEffect(() => {
		const displayTargetId = camera?.entityData?.displayTargetId;
		const displayType = camera?.entityData?.displayType;

		if (forReplay || !displayTargetId || displayType?.toLowerCase() !== "facility") {
			setFacilityPlacement(null);
			return;
		}

		facilityService.getEntityDisplayTarget(camera.entityType, camera.id, displayTargetId, (err, res) => {
			if (err || !res?.success) {
				setFacilityPlacement(null);
				return;
			}

			setFacilityPlacement(res.result);
		});
	}, [
		camera?.id,
		camera?.entityType,
		camera?.entityData?.displayTargetId,
		camera?.entityData?.displayType,
		forReplay
	]);

	useEffect(() => {
		// This if condition subscribes to the FOV stream for the camera ID when the persisted state contains the FOV (only in the map-app and events-app).
		if (activeFOVs && FOVSwitch) {
			dispatch(showFOV(camera?.id));
		}
		// In the Events app, when the Camera Profile or Facility Profile is closed, their icons are also cleared. Therefore, when the Camera Profile is closed, the cleanup below will remove the FOV as well.
		return () => {
			if (camera?.fov && appId === "events-app" && activeFOVs) {
				dispatch(hideFOV(camera.id));
			}
		};
	}, [activeFOVs, FOVSwitch, camera?.id, camera?.fov, appId]);

	useEffect(() => {
		if (!forReplay && widgetState?.length > 0) {
			const retryLiveCamera = widgetState?.find(element => element.id === "live_camera");
			if (retryLiveCamera.enabled && !context.liveCamera) {
				dispatch(startLiveCameraStream(contextId, "camera", "profile"));
			}
		}
	}, [widgetState]);

	const handleScroll = () => {
		$(".cb-profile-wrapper").on("resize scroll", () => {
			const elementTop = $(".summary-wrapper").offset().top;
			const elementBottom = elementTop + $(".summary-wrapper").outerHeight() + 120; // offset for app/navigation bar
			const viewportTop = $(".cb-profile-wrapper").scrollTop();
			const profileHeight = $(".cb-profile-wrapper").height();
			const viewportBottom = viewportTop + $(".cb-profile-wrapper").height();
			const widgetsHeight = $(".widgets-container").height();
			const scrollLength = viewportBottom - elementBottom;
			const pctScrolled = Math.floor((viewportTop / scrollLength) * 100); // gets percentage scrolled

			if (!scrolledUp && pctScrolled > 1 && widgetsHeight > profileHeight - 66) {
				setScrolledUp(true);
			} else if (scrolledUp && pctScrolled < 2) {
				setScrolledUp(false);
			}
		});
	};

	const prevContext = usePrevious(context);

	useEffect(() => {
		if (
			!forReplay &&
			context &&
			!disableLinkedItems &&
			!prevContext?.entity?.fov &&
			context.entity.fov &&
			isFovVisible
		) {
			dispatch(startFOVItemStream(contextId, "profile"));
		}
	}, [context, isFovVisible]);

	const handleEditLayout = event => {
		event.preventDefault();
		setLayoutControlsOpen(true);
	};

	const handleCloseEditLayout = () => {
		setLayoutControlsOpen(false);
	};

	const handleExpand = () => {
		setScrolledUp(false);
		$(".cb-profile-wrapper").scrollTop(0);
	};

	const getWidgetStatus = widgetId => {
		const widgetConfig = widgetState;
		const widget = widgetConfig.find((widget, index) => {
			widget.index = index;
			return widget.id === widgetId;
		});

		return widget;
	};

	const showFloorPlanOnTargetClick = () => {
		const { entityData } = camera;
		if (entityData?.displayType === "facility" && floorPlansWithFacilityFeed !== null) {
			const floorPlanData = floorPlansWithFacilityFeed[entityData.displayTargetId];
			if (floorPlanData?.id === entityData?.displayTargetId) {
				dispatch(selectFloorPlanOn(floorPlanData, floorPlanData.facilityFeedId));
			}
		}
	};

	const handleFov = () => {
		if (activeFOVs) {
			dispatch(hideFOV(camera.id));
			dispatch(updatePersistedState(appId, "activeFOVs", { [camera.id]: null }));
		} else {
			dispatch(updatePersistedState(appId, "activeFOVs", { [camera.id]: { fovId: camera.fov } }));
			dispatch(subscribeFOVs([camera.id]));
		}
	};

	const getSummaryWidgetActions = () => {
		let actions = [];
		if (
			entityData?.displayType?.toLowerCase() === "facility" &&
			entityData?.displayTargetId &&
			facilityOption &&
			!facilityHidden
		) {
			actions.push({
				name: getTranslation("global.profiles.cameraProfile.facility"),
				nameText: "Facility",
				action: () => window.location.replace(`/facilities-app/#/?${entityData.displayTargetId}`)
			});
		}

		actions = [
			...actions,
			{
				...(FOVSwitch && {
					name: activeFOVs
						? getTranslation("global.profiles.cameraProfile.hideFOV")
						: getTranslation("global.profiles.cameraProfile.showFOV"),
					nameText: activeFOVs ? "Hide FOV" : "Show FOV",
					action: handleFov
				})
			},
			...(canManage && properties?.adminLink
				? [
						{
							name: getTranslation("global.profiles.cameraProfile.adminPage"),
							nameText: "Admin Page",
							action: () => window.open(properties.adminLink, "_blank", "noopener,noreferrer")
						}
					]
				: []),
			{
				name: getTranslation("global.profiles.cameraProfile.pinTo"),
				nameText: "Pin To",
				action: () => dispatch(openDialog("pinToDialog"))
			}
		];
		// can we edit a camera?
		if (canManage) {
			actions = [
				...actions,
				{
					name: getTranslation("global.profiles.cameraProfile.edit"),
					nameText: "Edit",
					action: () => dispatch(openDialog("cameraEditDialog"))
				}
			];
		}
		return actions;
	};

	const widgetRenderFilters = {
		map: selectedTab === "deviceConfiguration" ? true : !widgetsExpandable || !($(window).width() > 1023),
		files: !context?.attachments,
		activities: !(context?.activities || forReplay),
		linked_items: disableLinkedItems,
		live_camera: !liveCamera
	};

	const renderWidgets = widgetName => {
		switch (widgetName) {
			case "details":
				return (
					<DetailsWidget
						id={widgetName}
						key={`${contextId}-details`}
						details={displayProperties.length > 0 ? entityData?.properties : {}}
						displayProps={displayProperties}
						enabled={!widgetRenderFilters.details}
					/>
				);
			case "map":
				return <MapWidget id={widgetName} selectWidget={selectWidget} entityType={entityType} />;

			case "files":
				return (
					<FileWidget
						key={`${contextId}-files`}
						id={widgetName}
						canDelete={canManage}
						hasAccess={canAccess}
						selected={view === widgetName}
						selectWidget={selectWidget}
						contextId={contextId}
						subscriberRef="profile"
						widgetsExpandable={widgetsExpandable}
					/>
				);

			case "activities":
				return (
					<Activities
						id={widgetName}
						key={`${contextId}-activities`}
						canManage={canAccess}
						selected={view === widgetName}
						pageSize={5}
						selectWidget={selectWidget}
						unsubscribeFromFeed={unsubscribeFromFeed}
						contextId={contextId}
						subscriberRef="profile"
						readOnly={readOnly}
						forReplay={forReplay}
						endDate={endDate}
						widgetsExpandable={widgetsExpandable}
						profile="camera"
					/>
				);

			case "linked_items":
				return (
					<LinkedItemsWidget
						id={widgetName}
						selected={view === widgetName}
						expanded={false} // for styling differences between profile and expanded widget views
						canLink={canManage}
						disabledTypes={disabledLinkedItemTypes}
						selectWidget={selectWidget}
						view={view}
						contextId={contextId}
						subscriberRef={"profile"}
						unsubscribeFromFeed={unsubscribeFromFeed}
						selectFloor={selectFloorPlanOn}
						facilityFeedId={facilityFeedId}
						widgetsExpandable={widgetsExpandable}
					/>
				);

			case "live_camera":
				return (
					<LiveCameraWidget
						id={widgetName}
						key="camera-profile-view"
						selected={view === widgetName}
						expanded={false} // for styling differences between profile and expanded widget views
						canControl={canControl}
						selectWidget={selectWidget}
						contextId={contextId}
						subscriberRef={"profile"}
						readOnly={readOnly}
						widgetsExpandable={widgetsExpandable}
					/>
				);
			case "alerting_zones":
				return (
					<AlertingZonesWidget
						id={widgetName}
						key={contextId}
						selected={view === widgetName}
						selectWidget={selectWidget}
						contextId={contextId}
						readOnly={readOnly}
						widgetsExpandable={widgetsExpandable}
						updateEntity={updateCameraGlobally}
					/>
				);
			default:
				return null;
		}
	};

	if (context) {
		// Height of summary-info (consistent between scrolled and not scrolled state) + padding
		const scrollOffset = scrolledUp ? 167 : 0;
		// Dynamic offset for widget container when SummaryWidget is collapsed
		const widgetsContainerStyle = {
			top: scrollOffset
		};

		return (
			<div className="cb-profile-wrapper" style={{ height: "100%" }} data-testid="camera-profile">
				<div
					style={{ display: layoutControlsOpen ? "block" : "none" }}
					data-testid="camera-profile-layout-controls"
				>
					<ErrorBoundary>
						<LayoutControls
							close={handleCloseEditLayout}
							widgetOrder={widgetState}
							profile="camera"
							layoutControlsOpen={layoutControlsOpen}
						/>
					</ErrorBoundary>
				</div>
				<div style={{ display: layoutControlsOpen ? "none" : "block" }}>
					<ErrorBoundary>
						<SummaryWidget
							id={contextId}
							readOnly={readOnly}
							contextId={contextId}
							name={name}
							type={subtype ? subtype : type}
							geometry={cameraGeometry}
							description={description}
							scrolledUp={scrolledUp}
							handleExpand={handleExpand}
							selectFloor={showFloorPlanOnTargetClick}
							actions={getSummaryWidgetActions()}
							facilityPlacement={facilityPlacement}
						/>
					</ErrorBoundary>
					{!scrolledUp && (
						<div className="layout-control-button" data-testid="camera-profile-layout-button-section">
							<a
								className="cb-font-link"
								onClick={handleEditLayout}
								data-testid="camera-profile-edit-layout-button"
							>
								<Translate value="global.profiles.cameraProfile.editProfileLayout" />
							</a>
						</div>
					)}
					<div
						className="widgets-container"
						style={widgetsContainerStyle}
						data-testid="camera-profile-widgets"
					>
						{widgetState
							.filter(element => getWidgetStatus(element.id))
							.map(element => {
								const { id, enabled } = element;

								if (widgetRenderFilters[id]) {
									return false;
								}
								if (!enabled) {
									return false;
								}
								return <ErrorBoundary key={id}>{renderWidgets(id)}</ErrorBoundary>;
							})}
						<CameraDialog
							camera={camera}
							close={() => dispatch(closeDialog("cameraEditDialog"))}
							update={updateCamera}
							dir={dir}
						/>
						<PinToDialog
							close={() => dispatch(closeDialog("pinToDialog"))}
							entity={camera}
							addRemoveFromCollections={addRemoveFromCollections}
							addRemoveFromEvents={addRemoveFromEvents}
							createCollection={createCollection}
							openDialog={openDialog}
							closeDialog={closeDialog}
							canManageEvents={canManageEvents}
							canPinToCollections={canPinToCollections}
							dir={dir}
						/>
					</div>
				</div>
			</div>
		);
	} else {
		return <div />;
	}
};

CameraProfile.propTypes = propTypes;
CameraProfile.defaultProps = defaultProps;

export default memo(CameraProfile, (prevProps, nextProps) => {
	if (!isEqual(prevProps, nextProps)) {
		return false;
	}
});
